import type { EbmReportResponse } from './api';

export const EBM_MESICE = [
  'leden',
  'únor',
  'březen',
  'duben',
  'květen',
  'červen',
  'červenec',
  'srpen',
  'září',
  'říjen',
  'listopad',
  'prosinec',
];

export function ebmMonthLabel(rok: number, mesic: number): string {
  return `${EBM_MESICE[mesic - 1] ?? mesic} ${rok}`;
}

/** Percentage as typed, without a trailing „,00" on whole numbers. */
export function ebmPctLabel(p: number): string {
  return `${p.toFixed(p % 1 === 0 ? 0 : 2).replace('.', ',')} %`;
}

/** Human wording for the column a rule moves or measures by. */
const SLOUPEC_2P: Record<string, string> = { N: 'nákladů', V: 'výnosů' };
const SLOUPEC_4P: Record<string, string> = { N: 'náklady', V: 'výnosy' };

/**
 * Active rules of a report, read off the outgoing presun rows — one entry per
 * rule, in line order. Lets the print sheet and the CSV explain why a cell is
 * reported lower than its účty add up to.
 */
export function collectEbmSplits(report: EbmReportResponse): { zdroj: string; cil: string; podil: string }[] {
  const out: { zdroj: string; cil: string; podil: string }[] = [];
  if (report.rozvaha) {
    for (const l of report.rozvaha.lines) {
      for (const p of l.presuny) {
        if (p.smer !== 'out') continue;
        const podil = p.druh === 'castka' ? `hodnota buňky ${p.odkazOzn}` : ebmPctLabel(p.procento);
        out.push({ zdroj: l.ozn, cil: p.ozn, podil });
      }
    }
    return out;
  }
  for (const l of report.lines) {
    for (const p of l.presuny) {
      if (p.smer !== 'out') continue;
      const podil =
        p.druh === 'castka'
          ? `${SLOUPEC_4P[p.sloupec] ?? ''} ve výši ${SLOUPEC_2P[p.odkazSloupec] ?? ''} buňky ${p.odkazOzn}`
          : p.procentoN === p.procentoV
            ? ebmPctLabel(p.procentoN)
            : `${ebmPctLabel(p.procentoN)} N · ${ebmPctLabel(p.procentoV)} V`;
      out.push({ zdroj: l.base, cil: p.ozn, podil });
    }
  }
  return out;
}

function csvCell(v: string | number): string {
  // Czech Excel: comma decimal; text fields quoted with doubled inner quotes.
  if (typeof v === 'number') return v.toFixed(2).replace('.', ',');
  return `"${v.replace(/"/g, '""')}"`;
}

/** Czech-Excel CSV (UTF-8 BOM, ';' delimiter, ',' decimal) of the report. */
export function buildReportCsv(report: EbmReportResponse): string {
  const rows: string[] = [];
  if (report.rozvaha) {
    // Rozvaha: cílová buňka → hodnota.
    rows.push(['Cílová buňka', 'Hodnota'].map(csvCell).join(';'));
    for (const l of report.rozvaha.lines) {
      rows.push([csvCell(l.ozn), csvCell(l.hodnota)].join(';'));
    }
    rows.push([csvCell('CELKEM'), csvCell(report.rozvaha.total)].join(';'));
  } else {
    // Výkaz: OZN → náklady / z toho − / výnosy.
    rows.push(['OZN', 'Náklady', 'z toho záporné', 'Výnosy'].map(csvCell).join(';'));
    for (const l of report.lines) {
      rows.push([csvCell(l.base), csvCell(l.naklady), csvCell(l.nakladyZaporne), csvCell(l.vynosy)].join(';'));
    }
    rows.push(
      [
        csvCell('CELKEM'),
        csvCell(report.totals.naklady),
        csvCell(report.totals.nakladyZaporne),
        csvCell(report.totals.vynosy),
      ].join(';'),
    );
  }
  const splits = collectEbmSplits(report);
  if (splits.length > 0) {
    rows.push('');
    rows.push([csvCell('Rozdělení buněk'), csvCell('Cíl'), csvCell('Podíl / částka')].join(';'));
    for (const s of splits) {
      rows.push([csvCell(s.zdroj), csvCell(s.cil), csvCell(s.podil)].join(';'));
    }
  }
  if (report.unmapped.length > 0) {
    rows.push('');
    rows.push([csvCell('Nezařazené účty'), csvCell('Netto'), csvCell('OZN')].join(';'));
    for (const u of report.unmapped) {
      rows.push([csvCell(`${u.ucet} ${u.nazev ?? ''}`.trim()), csvCell(u.netto), csvCell(u.ozn ?? '')].join(';'));
    }
  }
  return '\uFEFF' + rows.join('\r\n');
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
